import { useEffect, useReducer, useRef, type DragEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, FileText, Loader2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { useToast } from 'components/ui/toast';
import { cn } from 'lib/utils';
import { formatDateTime } from 'utils/formatDate';
import { validateTechnicianImport, type TechnicianImportAnswer } from 'api/techniciansApi';
import type {
  ImportFileError,
  TechnicianImportApplyResponse as Report,
  TechnicianImportValidateResponse as Preview,
} from 'api/technicianImportContract';
import { useApplyTechnicianImport } from 'hooks/useTechnicians';
import EmptyState from 'components/EmptyState';
import { fileErrorMessage, ignoredRowCount } from '../importLabels';
import { useWorkbookDownload } from '../useWorkbookDownload';
import ImportPreview from './ImportPreview';
import ImportReport from './ImportReport';

const MAX_BYTES = 5 * 1024 * 1024;

type State =
  | { kind: 'idle'; dragOver: boolean }
  | { kind: 'analysing'; file: File }
  | { kind: 'fileError'; file: File; error: ImportFileError }
  | { kind: 'failed'; file: File; step: 'validate' | 'apply' }
  | { kind: 'preview'; file: File; preview: Preview; outdated: boolean }
  | { kind: 'importing'; file: File; preview: Preview }
  | { kind: 'report'; file: File; preview: Preview; report: Report }
  | { kind: 'sessionExpired' }
  | { kind: 'forbidden' };

type Action =
  | { type: 'drag'; over: boolean }
  | { type: 'reset' }
  | { type: 'analyse'; file: File }
  | { type: 'fileError'; file: File; error: ImportFileError }
  | { type: 'answer'; file: File; answer: TechnicianImportAnswer<Preview | Report> }
  | { type: 'import' }
  | { type: 'failed'; file: File; step: 'validate' | 'apply' };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'drag':
      return state.kind === 'idle' ? { kind: 'idle', dragOver: action.over } : state;
    case 'reset':
      return { kind: 'idle', dragOver: false };
    case 'analyse':
      return { kind: 'analysing', file: action.file };
    case 'fileError':
      return { kind: 'fileError', file: action.file, error: action.error };
    case 'import':
      return state.kind === 'preview' ? { kind: 'importing', file: state.file, preview: state.preview } : state;
    case 'failed':
      return { kind: 'failed', file: action.file, step: action.step };
    case 'answer': {
      const { answer, file } = action;
      if (answer.kind === 'sessionExpired' || answer.kind === 'forbidden') return { kind: answer.kind };
      if (answer.kind === 'fileError') return { kind: 'fileError', file, error: answer.error };
      // CA14: the recomputed preview replaces the old one under an amber banner
      if (answer.kind === 'conflict') return { kind: 'preview', file, preview: answer.preview, outdated: true };
      if (state.kind === 'importing') return { kind: 'report', file, preview: state.preview, report: answer.data as Report };
      return { kind: 'preview', file, preview: answer.data as Preview, outdated: false };
    }
    default:
      return state;
  }
};

interface ImportTechniciansModalProps {
  country: string;
  countryName: string;
  onClose: () => void;
  /** The menu button, focused again when the modal closes (CA22, M14). */
  returnFocusRef: RefObject<HTMLElement>;
}

/** F23 — drop, analyse (validate), preview, import (apply), report; one reducer drives it all. */
const ImportTechniciansModal = ({ country, countryName, onClose, returnFocusRef }: ImportTechniciansModalProps) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [state, dispatch] = useReducer(reducer, { kind: 'idle', dragOver: false });
  const titleRef = useRef<HTMLHeadingElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const apply = useApplyTechnicianImport();
  useEffect(() => () => abortRef.current?.abort(), []);
  const list = (terms: string[]) => new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(terms);

  const analyse = async (file: File) => {
    abortRef.current?.abort();
    // CA5: refused before any upload when the browser can already tell
    if (!file.name.toLowerCase().endsWith('.xlsx')) return dispatch({ type: 'fileError', file, error: { code: 'not_xlsx' } });
    if (file.size > MAX_BYTES) return dispatch({ type: 'fileError', file, error: { code: 'too_large' } });
    const controller = new AbortController();
    abortRef.current = controller;
    dispatch({ type: 'analyse', file });
    try {
      const answer = await validateTechnicianImport(file, country, controller.signal);
      if (!controller.signal.aborted) dispatch({ type: 'answer', file, answer });
    } catch {
      if (!controller.signal.aborted) dispatch({ type: 'failed', file, step: 'validate' });
    }
  };

  const cancelAnalysis = () => {
    abortRef.current?.abort();
    dispatch({ type: 'reset' });
  };

  const close = () => {
    if (state.kind === 'importing') return; // CA12: Escape, overlay and ✕ all land here
    abortRef.current?.abort();
    onClose();
  };

  const { download } = useWorkbookDownload();

  const preview = state.kind === 'preview' || state.kind === 'importing' ? state.preview : null;
  const toExcludeOrReinstate = preview ? preview.counts.toExclude + preview.counts.toReinstate : 0;
  const valid = preview ? preview.counts.additions + preview.counts.changes + toExcludeOrReinstate : 0;
  const ignored = preview ? ignoredRowCount(preview) : 0;
  const nothingAtAll = !!preview && valid === 0 && preview.counts.errors === 0 && preview.counts.notCreated === 0;

  const runImport = async () => {
    // CA12: the first click re-renders into `importing` before a second one can land
    if (state.kind !== 'preview' || valid === 0) return;
    const { file, preview: shown } = state;
    dispatch({ type: 'import' });
    try {
      const answer = await apply.mutateAsync({ file, country, fingerprint: shown.fingerprint });
      if (answer.kind === 'ok' && answer.data.counts.failed === 0) {
        const { created, updated, excluded, reinstated } = answer.data.counts;
        const terms = [
          created && t('{{count}} created', { count: created }),
          updated && t('{{count}} updated', { count: updated }),
          excluded && t('{{count}} banned', { count: excluded }),
          reinstated && t('{{count}} reinstated', { count: reinstated }),
        ].filter((term): term is string => !!term);
        const summary = list(terms);
        toast.success(
          created
            ? t('Import finished · {{summary}}. The created technicians received no message.', { summary })
            : t('Import finished · {{summary}}.', { summary })
        );
        onClose();
        return;
      }
      dispatch({ type: 'answer', file, answer });
    } catch {
      dispatch({ type: 'failed', file, step: 'apply' });
    }
  };

  const importLabel = () => {
    if (!preview) return '';
    if (nothingAtAll) return t('Nothing to import');
    if (valid === 0) return t('Import 0 row');
    const terms = [
      preview.counts.additions && t('{{count}} additions', { count: preview.counts.additions }),
      preview.counts.changes && t('{{count}} changes', { count: preview.counts.changes }),
      toExcludeOrReinstate && t('{{count}} bans', { count: toExcludeOrReinstate }),
    ].filter((term): term is string => !!term);
    const head = t('Import {{terms}}', { terms: list(terms) });
    return ignored ? t('{{head}} · {{ignored}}', { head, ignored: t('{{count}} rows ignored', { count: ignored }) }) : head;
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    dispatch({ type: 'drag', over: false });
    const file = event.dataTransfer.files?.[0];
    if (file) analyse(file);
  };

  const title =
    state.kind === 'report'
      ? t('Import finished with {{count}} failures', { count: state.report.counts.failed })
      : preview
        ? t('Import preview')
        : t('Import technicians · {{country}}', { country: countryName });
  const rowsRead = preview && t('{{count}} rows read', { count: preview.rowsRead });
  const subtitle = (() => {
    if (preview && 'file' in state) {
      const rows = preview.emptyRows
        ? t('{{read}} ({{ignored}})', { read: rowsRead, ignored: t('{{count}} empty rows ignored', { count: preview.emptyRows }) })
        : rowsRead;
      return t('{{file}} · {{rows}} · nothing is saved yet', { file: state.file.name, rows });
    }
    if (state.kind !== 'report') return t('An Excel file on the GPS template. The preview shows everything before saving.');
    const importedAt = state.report.importedAt;
    return Number.isNaN(new Date(importedAt).getTime())
      ? state.file.name
      : t('{{file}} · imported on {{date}}', { file: state.file.name, date: formatDateTime(importedAt) });
  })();

  const templateLink = (
    <Button type="button" variant="outline" size="sm" className="self-start" onClick={() => download(country, false)}>
      {t('Download the template')}
    </Button>
  );

  const reasonId = 'import-disabled-reason';
  const canRedrop = state.kind === 'fileError' || state.kind === 'failed' || state.kind === 'preview';
  const importing = state.kind === 'importing';

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent
        aria-busy={state.kind === 'analysing' || importing}
        className={cn(
          'flex max-h-[90vh] max-w-[960px] flex-col gap-0 overflow-hidden p-0',
          importing && '[&>button:last-child]:pointer-events-none [&>button:last-child]:opacity-40'
        )}
        // Another file can be dropped over the modal after an error or on the preview
        onDragOver={(event) => canRedrop && event.preventDefault()}
        onDrop={(event) => canRedrop && onDrop(event)}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          titleRef.current?.focus(); // M14
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocusRef.current?.focus();
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          data-testid="import-file-input"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) analyse(file);
          }}
        />
        <div className="flex shrink-0 flex-col gap-1 px-5 pb-3 pt-[18px] pr-12">
          <DialogTitle ref={titleRef} tabIndex={-1} className="text-[17px] focus:outline-none">
            {title}
          </DialogTitle>
          <DialogDescription className="text-[13px]">{subtitle}</DialogDescription>
        </div>

        <div className={cn('flex min-h-0 flex-1 flex-col gap-2.5 px-5 pb-2', preview ? 'overflow-hidden' : 'overflow-y-auto')}>
          {state.kind === 'idle' && (
            <>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                onDragOver={(event) => {
                  event.preventDefault();
                  if (!state.dragOver) dispatch({ type: 'drag', over: true });
                }}
                onDragLeave={() => dispatch({ type: 'drag', over: false })}
                onDrop={onDrop}
                className={cn(
                  'flex flex-col items-center gap-2 rounded-xl px-4 py-[34px] text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  state.dragOver
                    ? 'border-2 border-dashed border-sky-500 bg-sky-50 dark:bg-sky-950/40'
                    : 'border border-dashed border-zinc-300 dark:border-zinc-700'
                )}
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  <Upload className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <span className={cn('text-[13.5px] font-medium', state.dragOver && 'text-sky-800 dark:text-sky-300')}>
                  {state.dragOver ? t('Drop to analyse') : t('Drop the Excel file or browse')}
                </span>
                <span className="text-xs text-muted-foreground">{t('.xlsx · 5 MB and 1,000 rows at most · GPS template')}</span>
              </button>
              {templateLink}
            </>
          )}

          {state.kind === 'analysing' && (
            <div className="flex flex-col items-center gap-2.5 rounded-xl border px-4 py-[34px] text-center" role="status">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              </span>
              <span className="text-[13.5px] font-medium">{t('Analysing {{file}}…', { file: state.file.name })}</span>
              <span className="text-xs text-muted-foreground">{t('Nothing is saved yet')}</span>
            </div>
          )}

          {(state.kind === 'fileError' || state.kind === 'failed') && (
            <div className="flex flex-col gap-2 rounded-xl border border-rose-400 px-4 py-3.5 dark:border-rose-500">
              <div className="flex items-center gap-2.5 text-[13.5px] font-medium">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                {state.file.name}
              </div>
              <p role="alert" className="flex items-start gap-1.5 text-[12.5px] text-rose-700 dark:text-rose-400">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {state.kind === 'fileError'
                  ? fileErrorMessage(t, state.error, countryName)
                  : state.step === 'validate'
                    ? t('The analysis did not complete. Check your connection and try again.')
                    : t('The import did not complete. Analyse the file again to see what is left to do.')}
              </p>
              {state.kind === 'fileError' && templateLink}
            </div>
          )}

          {state.kind === 'sessionExpired' && (
            <EmptyState tone="danger" icon={AlertCircle} title={t('Your session has expired.')} hint={t('Sign in again, then drop the file again.')} />
          )}

          {state.kind === 'forbidden' && (
            <EmptyState tone="danger" icon={AlertCircle} title={t('You are not allowed to import technicians.')} />
          )}

          {state.kind === 'preview' && state.outdated && (
            <div role="alert" className="flex items-center gap-2.5 rounded-lg bg-amber-50 px-3 py-2.5 text-[13px] font-medium text-amber-900 dark:bg-amber-950/50 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t('The base changed since the analysis: the preview was recomputed. Check it, then run the import again.')}
            </div>
          )}

          {preview && <ImportPreview key={preview.fingerprint} preview={preview} />}

          {state.kind === 'report' && <ImportReport report={state.report} preview={state.preview} />}
        </div>

        <div className="flex shrink-0 flex-col gap-2.5 border-t px-5 pb-[18px] pt-2.5">
          {state.kind === 'preview' && preview && preview.counts.errors > 0 && valid > 0 && (
            <p className="text-[12.5px] text-muted-foreground">
              {t('Rows to correct will not be imported. Fix them in your file, then import it again: what is already imported will not change.')}
            </p>
          )}
          {state.kind === 'preview' && valid === 0 && !nothingAtAll && (
            <p id={reasonId} className="flex items-center gap-1.5 text-[12.5px] text-rose-700 dark:text-rose-400">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t('No row can be imported: correct the rows shown in your file.')}
            </p>
          )}
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-amber-800 dark:text-amber-300">
              {importing && (
                <>
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('Do not close the window')}
                </>
              )}
            </span>
            <div className="flex gap-2">
              {state.kind !== 'report' && state.kind !== 'sessionExpired' && state.kind !== 'forbidden' && (
                <Button
                  type="button"
                  variant="outline"
                  aria-disabled={importing}
                  className={cn(importing && 'opacity-40')}
                  onClick={() => (state.kind === 'analysing' ? cancelAnalysis() : close())}
                >
                  {t('Cancel')}
                </Button>
              )}
              {canRedrop && (
                <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
                  {t('Choose another file')}
                </Button>
              )}
              {state.kind === 'failed' && (
                <Button type="button" variant="outline" onClick={() => analyse(state.file)}>
                  {t('Try again')}
                </Button>
              )}
              {state.kind === 'sessionExpired' && (
                // A reload re-validates the session, which lands on the login screen (M9)
                <Button type="button" onClick={() => window.location.reload()}>
                  {t('Sign in again')}
                </Button>
              )}
              {(state.kind === 'report' || state.kind === 'forbidden') && (
                <Button type="button" onClick={close}>
                  {t('Close')}
                </Button>
              )}
              {preview && (
                <Button
                  type="button"
                  aria-disabled={valid === 0 || importing}
                  aria-describedby={state.kind === 'preview' && valid === 0 && !nothingAtAll ? reasonId : undefined}
                  aria-busy={importing}
                  className={cn((valid === 0 || importing) && 'opacity-50')}
                  onClick={runImport}
                >
                  {importing && <Loader2 className="animate-spin" aria-hidden="true" />}
                  {importing ? t('Import in progress…') : importLabel()}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportTechniciansModal;
